import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSubscription } from '../context/SubscriptionContext';

import { formatPrice } from '../utils/formatHelper';
import placeholderImage from '../assets/images/placeholder.jpg';
import { showSuccess, showError, showInfo } from '../utils/toastHelper';

function FeatureProduct({ product }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isManualNavigation, setIsManualNavigation] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [progress, setProgress] = useState(0);
  const slideIntervalRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const { addToPackage, removeFromPackage, updateQuantity, items } = useSubscription();
  const [quantity, setQuantity] = useState(1);
  const [inputValue, setInputValue] = useState('1');

  const isInPackage = items.some(item => item.id === product.id);
  const currentItem = items.find(item => item.id === product.id);

  // Get all available images including mainImage
  // Deduplicate images based on URL, preferring objects with metadata
  const allImages = React.useMemo(() => {
    const uniqueImagesMap = new Map();

    // 1. Add mainImage logic first to ensure it stays at the top
    if (product.mainImage) {
      uniqueImagesMap.set(product.mainImage, { url: product.mainImage });
    }

    // 2. Add/Update from product.images
    // This preserves the mainImage position (first) but updates its content with metadata if available
    if (product.images && Array.isArray(product.images)) {
      product.images.forEach(img => {
        if (img && img.url) {
          uniqueImagesMap.set(img.url, img);
        }
      });
    }

    return Array.from(uniqueImagesMap.values());
  }, [product.mainImage, product.images]);

  // Responsive davranış için pencere boyutu değişikliklerini izle
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (currentItem) {
      setQuantity(currentItem.quantity);
      setInputValue(currentItem.quantity.toString());
    } else {
      setQuantity(1);
      setInputValue('1');
    }
  }, [currentItem]);

  // Resim değiştiğinde progress'i sıfırla
  useEffect(() => {
    if ((isHovered || isFocused) && allImages.length > 1 && !isManualNavigation) {
      setProgress(0);
    }
  }, [currentImageIndex, isHovered, isFocused, allImages.length, isManualNavigation]);

  useEffect(() => {
    // Progress animasyonunu temizle
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    if ((isHovered || isFocused) && allImages.length > 1 && !isManualNavigation) {
      // Progress animasyonu başlat (2 saniyede 0'dan 100'e)
      const progressStep = 100 / (2000 / 16); // 16ms per frame (60fps)
      progressIntervalRef.current = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            return 0;
          }
          return Math.min(prev + progressStep, 100);
        });
      }, 16);

      // Resim değiştirme interval'i
      slideIntervalRef.current = setInterval(() => {
        setCurrentImageIndex(prev => (prev + 1) % allImages.length);
      }, 2000);
    } else {
      if (slideIntervalRef.current) {
        clearInterval(slideIntervalRef.current);
        slideIntervalRef.current = null;
      }
      setProgress(0); // Progress'i sıfırla
      if (!isHovered && !isFocused) {
        setCurrentImageIndex(0);
        setIsManualNavigation(false); // Reset manual navigation when mouse leaves
      }
    }

    return () => {
      if (slideIntervalRef.current) {
        clearInterval(slideIntervalRef.current);
        slideIntervalRef.current = null;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [isHovered, isFocused, allImages.length, isManualNavigation]);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleManualImageChange = (index) => {
    setCurrentImageIndex(index);
    setIsManualNavigation(true);
    setProgress(0); // Progress'i sıfırla
  };

  const handlePrevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex(prev => (prev === 0 ? allImages.length - 1 : prev - 1));
    setIsManualNavigation(true);
    setProgress(0); // Progress'i sıfırla
  };

  const handleNextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex(prev => (prev + 1) % allImages.length);
    setIsManualNavigation(true);
    setProgress(0); // Progress'i sıfırla
  };

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity >= 1) {
      // Stok kontrolü yap
      if (product.stock && newQuantity > product.stock) {
        showError(`Üzgünüz, bu üründen sadece ${product.stock} adet stokta bulunmaktadır.`);
        return;
      }

      setQuantity(newQuantity);
      updateQuantity(product.id, newQuantity);
    }
  };

  const handleQuantityInputChange = (e) => {
    // Sadece local state'i güncelle, context'i güncelleme
    const value = e.target.value;
    setInputValue(value);
  };

  const handleQuantityInputBlur = (e) => {
    // Input'tan çıkıldığında değeri doğrula ve güncelle
    let numValue = parseInt(e.target.value);
    
    // Geçersiz veya boş değer
    if (isNaN(numValue) || numValue < 1) {
      numValue = 1;
    }
    
    // Stok kontrolü
    if (product.stock && numValue > product.stock) {
      showError(`Üzgünüz, bu üründen sadece ${product.stock} adet stokta bulunmaktadır.`);
      numValue = product.stock;
    }
    
    setQuantity(numValue);
    setInputValue(numValue.toString());
    updateQuantity(product.id, numValue);
  };

  const handleQuantityInputKeyDown = (e) => {
    // Enter tuşuna basıldığında blur işlemini tetikle
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const handlePackageToggle = (e) => {
    e.preventDefault();
    if (isInPackage) {
      removeFromPackage(product.id);
      showInfo('Ürün paketten çıkarıldı');
      setQuantity(1);
    } else {
      // Stok kontrolü yap
      if (!product.stock || product.stock <= 0) {
        showError('Bu ürün şu anda stokta bulunmamaktadır. Lütfen daha sonra tekrar deneyiniz veya benzer bir ürün seçiniz.');
        return;
      }

      addToPackage({ ...product, quantity: 1 });
      showSuccess('Ürün pakete eklendi');
    }
  };

  return (
    <div className="col">
      <div
        className="card h-100 product-card border-0"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        tabIndex="0"
      >
        <div className="card-img-wrapper position-relative">
          <img
            className="card-img-top product-image"
            alt={allImages[currentImageIndex]?.metadata?.alt || product.name}
            src={allImages[currentImageIndex]?.url || placeholderImage}
          />

          {/* Navigation Arrows - Glassmorphism Style */}
          {(isHovered || isFocused || isMobile) && allImages.length > 1 && (
            <>
              <button
                className="image-nav-arrow image-nav-prev"
                onClick={handlePrevImage}
                aria-label="Previous image"
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <button
                className="image-nav-arrow image-nav-next"
                onClick={handleNextImage}
                aria-label="Next image"
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </>
          )}

          {/* Badges - Pill Shaped */}
          <div className="position-absolute top-0 end-0 p-3 d-flex flex-column align-items-end gap-2">
            {product.discountValue && parseFloat(product.discountValue) > 0 && (
              <span className="badge badge-pill bg-orange shadow-sm">
                {product.discountType === 'PERCENTAGE' ?
                  `%${parseFloat(product.discountValue).toFixed(0)}` :
                  `${formatPrice(parseFloat(product.discountValue))}`} <span className="fw-normal">İNDİRİM</span>
              </span>
            )}
            {isInPackage && (
              <span className="badge badge-pill bg-success shadow-sm">
                <i className="fas fa-check me-1"></i>
                PAKETTE
              </span>
            )}
            {product.stock !== undefined && product.stock <= 5 && product.stock > 0 && (
              <span className="badge badge-pill bg-warning text-dark shadow-sm">
                SON {product.stock}
              </span>
            )}
            {product.stock !== undefined && product.stock <= 0 && (
              <span className="badge badge-pill bg-danger shadow-sm">
                TÜKENDİ
              </span>
            )}
          </div>

          {/* Story-Style Image Indicators */}
          {allImages.length > 1 && (
            <div className="story-indicators">
              {allImages.map((_, index) => {
                const isActive = index === currentImageIndex;
                const currentProgress = isActive ? progress : (index < currentImageIndex ? 100 : 0);
                
                return (
                <div
                  key={index}
                    className={`story-bar ${isActive ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleManualImageChange(index);
                  }}
                  >
                    <div 
                      className="story-bar-progress"
                      style={{ width: `${currentProgress}%` }}
                />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card-body d-flex flex-column p-3">
          <div className="flex-grow-1">
            <h5 className="card-title text-olive mb-2 product-title text-center">
              {product.name}
            </h5>

            <div className="price-container mb-3 d-flex justify-content-center align-items-baseline gap-2">
              {product.discountedPrice ? (
                <>
                  <span className="discounted-price text-orange fw-bold">
                    {formatPrice(product.discountedPrice)}
                  </span>
                  <span className="original-price text-decoration-line-through text-muted">
                    {formatPrice(product.price)}
                  </span>
                </>
              ) : (
                <span className="price fw-bold text-dark">
                  {formatPrice(product.price)}
                </span>
              )}
            </div>
          </div>

          <div className="action-area mt-auto">
            {isInPackage ? (
              <div className="d-flex justify-content-center align-items-center gap-2">
                <div className="quantity-pill shadow-inner">
                  <button
                    className="btn btn-link quantity-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      const newQuantity = quantity - 1;
                      if (newQuantity === 0) {
                        handlePackageToggle(e);
                      } else {
                        handleQuantityChange(newQuantity);
                      }
                    }}
                  >
                    <i className="fas fa-minus small"></i>
                  </button>
                  <input
                    type="number"
                    className="quantity-input"
                    value={inputValue}
                    onChange={handleQuantityInputChange}
                    onBlur={handleQuantityInputBlur}
                    onKeyDown={handleQuantityInputKeyDown}
                    min="1"
                    max={product.stock || 999}
                  />
                  <button
                    className="btn btn-link quantity-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      const newQuantity = quantity + 1;
                      if (product.stock && newQuantity > product.stock) {
                        showError(`Sadece ${product.stock} adet stokta var.`);
                        return;
                      }
                      handleQuantityChange(newQuantity);
                    }}
                    disabled={product.stock && quantity >= product.stock}
                  >
                    <i className="fas fa-plus small"></i>
                  </button>
                </div>
                <button
                  className="btn btn-outline-danger btn-circle shadow-sm"
                  onClick={handlePackageToggle}
                  title="Paketten Çıkar"
                >
                  <i className="fas fa-trash-alt"></i>
                </button>
              </div>
            ) : (
              <button
                className="btn btn-orange w-100 rounded-pill shadow-sm fw-medium letter-spacing-1"
                onClick={handlePackageToggle}
                disabled={product.stock !== undefined && product.stock <= 0}
              >
                {product.stock !== undefined && product.stock <= 0 ? 'TÜKENDİ' : 'PAKETE EKLE'}
              </button>
            )}
          </div>
        </div>
      </div>

      <style>
        {`
          /* Chrome, Safari, Edge, Opera */
          input[type="number"]::-webkit-inner-spin-button,
          input[type="number"]::-webkit-outer-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          /* Firefox */
          input[type="number"] {
            -moz-appearance: textfield;
          }

          .product-card {
            border-radius: 16px;
            background-color: #f4f3e9;
            transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
            overflow: hidden;
          }
          
          .product-card:hover {
            transform: translateY(-6px);
            background-color: #fff; /* Slight lift effect */
            box-shadow: 0 12px 24px rgba(139, 147, 100, 0.15), 0 4px 8px rgba(0,0,0,0.05);
          }

          /* Image Area */
          .card-img-wrapper {
            height: 260px;
            overflow: hidden;
            background-color: #f4f3e9;
            border-bottom: 1px solid rgba(0,0,0,0.03);
            position: relative;
          }
          
          .product-image {
            width: 100%;
            height: 100%;
            object-fit: contain;
            padding: 1.5rem;
            transition: transform 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
            mix-blend-mode: multiply;
          }
          
          .product-card:hover .product-image {
            transform: scale(1.08); /* Gentle zoom */
          }

          /* Navigation Arrows */
          .image-nav-arrow {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            background: rgba(255, 255, 255, 0.6);
            backdrop-filter: blur(4px);
            border: 1px solid rgba(255,255,255,0.8);
            border-radius: 50%;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            opacity: 0;
            transition: all 0.2s ease;
            color: var(--kito-olive, #4a5d23);
            z-index: 10;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          
          .product-card:hover .image-nav-arrow,
          .image-nav-arrow:focus {
            opacity: 1;
          }
          
          .image-nav-arrow:hover {
            background: #fff;
            transform: translateY(-50%) scale(1.1);
          }
          
          .image-nav-prev { left: 10px; }
          .image-nav-next { right: 10px; }

          /* Story Indicators */
          .story-indicators {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            display: flex;
            gap: 4px;
            padding: 8px 12px;
            background: linear-gradient(to top, rgba(0,0,0,0.05), transparent);
          }
          
          .story-bar {
            height: 3px;
            flex: 1;
            background-color: rgba(0,0,0,0.15);
            border-radius: 2px;
            cursor: pointer;
            transition: background-color 0.2s ease;
            position: relative;
            overflow: hidden;
          }
           .product-card:hover .story-bar {
             background-color: rgba(0,0,0,0.2);
           }
          
          .story-bar.active {
            background-color: rgba(0,0,0,0.2);
          }
          
          .story-bar-progress {
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            background-color: var(--kito-orange, #e67e22);
            border-radius: 2px;
            transition: width 0.1s linear;
            box-shadow: 0 0 4px rgba(230, 126, 34, 0.4);
          }
          
          .story-bar.active .story-bar-progress {
            background-color: var(--kito-orange, #e67e22);
          }

          /* Badges */
          .badge-pill {
            border-radius: 50px;
            font-weight: 600;
            font-size: 0.7rem;
            padding: 0.4em 0.8em;
            letter-spacing: 0.5px;
          }

          /* Product Info */
          .product-title {
            font-size: 1rem;
            font-weight: 600;
            line-height: 1.3;
            height: 2.6em; /* Fixed height for 2 lines */
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          
          .price-container {
            font-family: 'Inter', sans-serif; /* Ensure number font is clean */
          }
          .discounted-price, .price {
            font-size: 1.2rem;
          }
          .original-price {
            font-size: 0.9rem;
            opacity: 0.6;
          }

          /* Buttons & Inputs */
          .letter-spacing-1 { letter-spacing: 0.5px; font-size: 0.85rem; }
          
          .quantity-pill {
            display: flex;
            align-items: center;
            background-color: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 50px;
            padding: 2px;
            height: 38px;
            box-shadow: inset 0 2px 4px rgba(0,0,0,0.03);
          }
          
          .quantity-btn {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            color: var(--kito-olive, #4a5d23);
            text-decoration: none;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: background 0.2s;
          }
          .quantity-btn:hover {
            background-color: rgba(0,0,0,0.05);
          }
          
          .quantity-input {
            width: 40px;
            border: none;
            background: transparent;
            text-align: center;
            font-weight: 600;
            color: var(--kito-olive, #4a5d23);
          }
          
          .btn-circle {
             width: 38px;
             height: 38px;
             border-radius: 50%;
             display: flex;
             align-items: center;
             justify-content: center;
             padding: 0;
          }

          /* Mobile Adjustments */
          @media (max-width: 768px) {
            .card-img-wrapper {
              height: 200px;
            }
            .product-title {
              font-size: 0.9rem;
            }
            .discounted-price, .price {
              font-size: 1.1rem;
            }
            .story-indicators {
              padding-bottom: 6px;
            }
            .image-nav-arrow {
              opacity: 1; /* Always visible on mobile if relevant */
              width: 28px;
              height: 28px;
              background: rgba(255,255,255,0.8);
            }
          }
        `}
      </style>
    </div>
  );
}

export default FeatureProduct;